import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Item, Row } from 'components/ui/table/elements';
import ArrayConfiguration from 'pages/products/configuration/ArrayConfiguration';
import DateTimeTableCell from 'pages/products/configuration/DateTimeTableCell';
import { getRuleConfig } from 'helpers/rulesHelper';
import styled from 'styled-components';
import translate from 'components/hoc/translate';
import utils from 'helpers/utils';
import validations from 'helpers/forms/validations';
import SelectField from 'components/input/SelectField/SelectField';

const StyledInputWrapper = styled.div`
    &>div:not(:first-of-type) {
        margin-top: 10px;
    }
`;

const ATTRIBUTE_PROPERTY = 'attribute';
const INPUT_TYPE = 'date';
const VALUE_PROPERTY = 'value';

class DatesRule extends Component {
    config = null;

    state = {
        prepared: false,
    };

    componentDidMount() {
        this.config = getRuleConfig(utils.firstCharToLowerCase(this.props.type)).config;

        this.reinitialize();
    }

    componentDidUpdate(prevProps) {
        const {
            formValues,
        } = this.props;

        const diffObjects = utils.diffObjects(formValues, prevProps.formValues);

        if (!utils.isEmpty(diffObjects)) {
            this.updateFormState();
        }
    }

    generateValues = (values) => {
        if (utils.isEmpty(values)) {
            return null;
        }

        const generated = utils.map(values, (value) => {
            return {
                id: Math.floor(Math.random() * Date.now()),
                date: value,
            };
        });

        return generated;
    }

    reinitialize = () => {
        const {
            initialize,
            value,
        } = this.props;

        this.setState({
            prepared: false,
        });

        const initialState = {};

        initialState[ATTRIBUTE_PROPERTY] = value[ATTRIBUTE_PROPERTY] || null;
        initialState[VALUE_PROPERTY] = this.generateValues(value[VALUE_PROPERTY]);

        initialize(initialState);

        this.setState({
            prepared: true,
        });
    }

    updateFormState = (values) => {
        const {
            formValues: stateValues,
            onUpdate,
        } = this.props;

        const formValues = { ...stateValues };

        if (values) {
            formValues[VALUE_PROPERTY] = values.map((valueObject) => {
                return valueObject.date;
            });
        }

        return onUpdate(formValues);
    }

    onAddValue = (name) => {
        const {
            change,
            formValues: {
                value,
            },
        } = this.props;

        const values = [];

        if (!utils.isEmpty(value)) {
            values.push(...value);
        }

        values.push({
            id: Math.floor(Math.random() * Date.now()),
            date: '',
        });

        change(name, values);
    }

    onChangeValue = (event, eventValue, values) => {
        event.preventDefault();

        const {
            change,
            touch,
        } = this.props;

        const { id, type } = event.target;

        const newValues = utils.map(values, (valueObject) => {
            // Checks for date input type that returns a 10 character string from the event and adds time from the previous date.
            if (valueObject.id == id && type) {
                const time = valueObject.date.slice(-8);

                valueObject.date = `${ eventValue } ${ time }`;
            } else if (valueObject.id == id) {
                valueObject.date = eventValue;
            }

            return valueObject;
        });

        change(VALUE_PROPERTY, newValues);
        touch(VALUE_PROPERTY);

        this.updateFormState(newValues);
    }

    onDeleteValue = (id) => {
        const { change, formValues } = this.props;
        const { value: stateValue } = formValues;

        let newValues = [
            ...stateValue,
        ];

        if (newValues.length > 1) {
            newValues = newValues.filter((value) => {
                return value.id !== id;
            });
        } else {
            newValues = null;
        }

        change(VALUE_PROPERTY, newValues);

        this.updateFormState(newValues);
    }

    render() {
        const {
            form,
            translate,
            type,
        } = this.props;

        const { prepared } = this.state;

        if (!this.config || !prepared) {
            return null;
        }

        const choices = [
            {
                id: 'destination_arrival_date',
                name: 'resources.bookings.fields.destinationArrivalDate',
            },
            {
                id: 'destination_departure_date',
                name: 'resources.bookings.fields.destinationDepartureDate',
            },
        ];

        return (
            <StyledInputWrapper>
                <Row>
                    <Item>
                        <Field
                            key={ `${ form }-input-${ ATTRIBUTE_PROPERTY }` }
                            choices={ choices }
                            component={ SelectField }
                            label={ translate(`resources.products.${ ATTRIBUTE_PROPERTY }`) }
                            name={ ATTRIBUTE_PROPERTY }
                        />
                    </Item>
                </Row>
                <Row>
                    <Item>
                        <Field
                            key={ `${ form }-input-${ VALUE_PROPERTY }` }
                            component={ ArrayConfiguration }
                            fields={ [
                                INPUT_TYPE,
                            ] }
                            inputComponent={ DateTimeTableCell }
                            id={ type }
                            name={ VALUE_PROPERTY }
                            onAdd={ this.onAddValue }
                            onChange={ this.onChangeValue }
                            onDelete={ this.onDeleteValue }
                            type='dates'
                            title={ translate(`resources.products.${ ATTRIBUTE_PROPERTY }.title`) }
                        />
                    </Item>
                </Row>
            </StyledInputWrapper>
        );
    }
}

DatesRule.propTypes = {
    change: PropTypes.func.isRequired,
    form: PropTypes.string.isRequired,
    formValues: PropTypes.shape({
        attribute: PropTypes.string,
        value: PropTypes.arrayOf(PropTypes.object),
    }),
    initialize: PropTypes.func.isRequired,
    onUpdate: PropTypes.func.isRequired,
    touch: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
    type: PropTypes.string.isRequired,
    value: PropTypes.shape({
        attribute: PropTypes.string,
        value: PropTypes.arrayOf(PropTypes.string),
    }),
};

const mapStateToProps = (state, props) => {
    const {
        id,
        type,
    } = props;

    const form = `DatesComponent-${ type }-${ id }`;

    return {
        form: form,
        formValues: getFormValues(form)(state),
    };
};

const enhance = compose(
    translate,
    connect(mapStateToProps, null),
    reduxForm({
        validate: function (values, props) {
            let errors = {};

            utils.each(values, (value, property) => {
                if (utils.isArray(value)) {
                    const arrayValues = [
                        ...value,
                    ];

                    utils.each(arrayValues, (valueObject) => {
                        let result = validations.required(null, values, props);

                        if (valueObject.date) {
                            result = validations.required(valueObject.date, values, props);
                        }

                        if (result) {
                            errors[property] = result;
                        }
                    });
                    return;
                }

                if (!value) {
                    errors[property] = validations.required(null, values, props);
                    return;
                }

                errors[property] = validations.required(value, values, props);
            });

            return errors;
        },
    }),

);

export default enhance(DatesRule);


// WEBPACK FOOTER //
// ./src/pages/discounts/rules/types/DatesRule.js